// The script library's REST calls. Presets ship with the server; saved scripts belong to the
// signed-in Storyteller. Using a script in a game is a separate thing — that goes over the socket.

import { parseScriptJson } from "@cc/shared";
import type { Script } from "@cc/shared";
import { api as call } from "./api.js";

export type LibraryScript = { id: string; name: string; json: unknown[] };
export type Library = { presets: LibraryScript[]; saved: LibraryScript[] };

export const fetchLibrary = (): Promise<Library> => call<Library>("/api/scripts");

/** Creates a saved script, or overwrites `id` when given. */
export async function saveToLibrary(input: { id?: string; name: string; json: unknown[] }): Promise<LibraryScript> {
  return (await call<{ script: LibraryScript }>("/api/scripts", { method: "POST", body: input })).script;
}

export async function deleteFromLibrary(id: string): Promise<void> {
  await call(`/api/scripts/${id}`, { method: "DELETE" });
}

// --- script JSON helpers -------------------------------------------------------------------

const isMeta = (e: unknown): boolean =>
  !!e && typeof e === "object" && (e as Record<string, unknown>)["id"] === "_meta";

export const roleCount = (json: unknown[]): number => json.filter((e) => !isMeta(e)).length;

/** Sets the script's name, keeping whatever else (author, etc.) its `_meta` entry carried. */
export function withName(json: unknown[], name: string): unknown[] {
  const meta = json.find(isMeta) as Record<string, unknown> | undefined;
  return [{ ...meta, id: "_meta", name }, ...json.filter((e) => !isMeta(e))];
}

/** The parsed script, or null when the stored JSON can't be read any more. */
export function safeScript(json: unknown[]): Script | null {
  try {
    return parseScriptJson(json);
  } catch {
    return null;
  }
}
